import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { api, generateQueryKey } from "@/lib/api";
import {
  buildBuyingGroupRequestParams,
  createBuyingGroupQueryKeyPart,
  shouldEnableBuyingGroupQuery,
} from "@/lib/buying-group-utils";
import type { BuyingGroupMode } from "./use-account-activities";

export interface StageTransition {
  timestamp: string;
  event_name: string;
}

export interface Contact {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email_address: string | null;
  job_title: string | null;
  relation_sources: string[];
}

export interface Opportunity {
  id: string;
  name: string;
  stage_name: string;
  created_date: string;
  close_date: string | null;
  account_id: string;
  customer_org_id: string;
  source_record_id: string;
  source_record_type: string;
  type: string;
  stage_transitions: StageTransition[];
  contacts: Contact[];
  is_won: boolean;
  is_closed: boolean;
  // Touchpoint metrics (calculated via MetricRenderer)
  touchpoint_count?: number;
  first_touchpoint_date?: string | null;
  last_touchpoint_date?: string | null;
}

/**
 * Fetches opportunity data for an account, with support for various filtering modes.
 *
 * This hook uses a dual-filtering approach in custom buying group mode:
 * 1. For touchpoint filtering: Uses personIds to show all touchpoints related to selected people
 * 2. For opportunity display: Uses customGroupOpportunityIds to only show explicitly selected opportunities
 *    in the minimap and touchpoint table
 *
 * This approach maintains all touchpoints for context while giving users direct control over
 * which opportunity bars and stage progressions are shown.
 */
export function useAccountOpportunities(
  accountId?: string,
  opportunityIds?: string[],
  personIds?: string[],
  buyingGroupMode: BuyingGroupMode = "default",
  // Parameter for explicit opportunity IDs from custom buying groups - used for opportunity visuals only
  customGroupOpportunityIds?: string[],
) {
  // Use the shared utility for consistent query key generation
  const queryKey = [
    ...generateQueryKey(
      `datahub/accounts/${encodeURIComponent(accountId || "")}/opportunities/`,
    ),
    createBuyingGroupQueryKeyPart({
      opportunityIds,
      personIds,
      buyingGroupMode,
      customGroupOpportunityIds,
    }),
  ];

  // Determine if query should be enabled using shared utility
  const shouldEnableQuery = useMemo(
    () =>
      shouldEnableBuyingGroupQuery({
        accountId,
        buyingGroupMode,
        customGroupOpportunityIds,
      }),
    [accountId, buyingGroupMode, customGroupOpportunityIds],
  );

  return useQuery<Opportunity[]>({
    queryKey,
    queryFn: () => {
      // Use the shared utility for consistent parameter building
      // Special case for opportunities endpoint in custom mode - we only want to use
      // the explicitly selected opportunity IDs, not the person IDs
      const params = buildBuyingGroupRequestParams({
        opportunityIds,
        // Only use personIds in non-custom mode
        personIds: buyingGroupMode !== "custom" ? personIds : undefined,
        buyingGroupMode,
        customGroupOpportunityIds,
      });

      return api.get<Opportunity[]>(
        `datahub/accounts/${encodeURIComponent(
          accountId || "",
        )}/opportunities/`,
        { params },
      );
    },
    // Only enable the query when needed - in custom mode, only when we have opportunity IDs
    enabled: shouldEnableQuery,
  });
}
